import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { useSearch } from "@/utils";

import { useOrganizationAddToTrackerMutation } from "../../../redux/organization/organization.api";
import { useTrackerDestinationLists } from "../../List/utils/useTrackerDestinationLists";
import { ContactSelectType } from "../schema/contacts";

interface UseOrganizationTrackerAssignmentParams {
	/** What a bulk "add all of these" would send — ignored when a single row asked. */
	getSelectionPayload: () => { selectType: ContactSelectType; ids?: number[] };
	openSnackbar: (message: string, duration?: number) => void;
}

/**
 * Adding companies to a profile tracker, from a row or from the selection footer.
 *
 * Extracted from `CompetitorsTable`, which is over the length ratchet and may only shrink: the
 * modal it drives owns six pieces of state and two handlers that the table itself never reads.
 */
export const useOrganizationTrackerAssignment = ({
	getSelectionPayload,
	openSnackbar,
}: UseOrganizationTrackerAssignmentParams) => {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);
	const [selectedTracker, setSelectedTracker] = useState<AutoCompleteItem | undefined>(undefined);
	/** Set when one row asked, so the confirmation adds that company instead of the selection. */
	const [singleOrganizationId, setSingleOrganizationId] = useState<number | null>(null);
	const { setSearchValue, query } = useSearch();

	const [addToTracker, { isLoading: isSaving }] = useOrganizationAddToTrackerMutation();

	const { lists, isLoading, isFetching } = useTrackerDestinationLists({
		searchValue: query,
		includeJobChange: false,
		active: true,
		skip: !isOpen,
	});

	const open = useCallback((organizationId?: number) => {
		if (organizationId) {
			setSingleOrganizationId(organizationId);
		}

		setIsOpen(true);
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
		setSingleOrganizationId(null);
		setSelectedTracker(undefined);
	}, []);

	const confirm = useCallback(async () => {
		if (!selectedTracker) {
			return;
		}

		try {
			const payload = singleOrganizationId
				? { selectType: ContactSelectType.CURRENT_PAGE, ids: [singleOrganizationId] }
				: getSelectionPayload();

			await addToTracker({ ...payload, listId: Number(selectedTracker.id) }).unwrap();
			openSnackbar(t("contacts.addedToTracker"));
		} catch {
			openSnackbar(t("basics.error"));
		} finally {
			close();
		}
	}, [
		selectedTracker,
		singleOrganizationId,
		getSelectionPayload,
		addToTracker,
		openSnackbar,
		close,
		t,
	]);

	return {
		isOpen,
		open,
		close,
		confirm,
		isSaving,
		lists,
		isLoadingLists: isLoading,
		isFetchingLists: isFetching,
		selectedTracker,
		setSelectedTracker,
		setSearchValue,
	};
};
